import { FileInfoDto, FileType, QueryFactory } from '@lmt/shared-types';
import { DXR } from '@lmt/unicorn-shared/app/common/devexpress/RichEdit/dx-richedit';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { openTemplateMenuId } from '@lmt/unicorn-shared/app/components/documents/richedit/OpenTemplatesRichEditMenuItem';
import {
  getFileNameWithFormat,
  isDocumentNew,
} from '@lmt/unicorn-shared/app/components/documents/richedit/richedit-helper';
import { unfocusRichEditor } from '@lmt/unicorn-shared/app/components/documents/richedit/unfocusRichEditor';
import { getTabId } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useEventCallback } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { RichEdit } from 'devexpress-richedit';
import { DocumentFormatApi } from 'devexpress-richedit/lib/client/model-api/formats/enum';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

type Props = {
  richEdit: RichEdit;
  patientId: number;
  dataBlockId: number | undefined;
  documentId: string | undefined;
  groupId: string | undefined;
  templateId: number | undefined | null;
  onNewDocumentId?: (newId: string) => void;
  afterPublishAction?: (newVersion: FileInfoDto) => void;
};

export const usePublishDocumentHandler = (props: Props) => {
  const dispatch = useUniDispatch();
  const modals = useModal();
  const queryClient = useQueryClient();
  const i18n = useScopedTranslation('documents');
  const location = useLocation();
  const tabId = getTabId(location);

  const {
    documentId: currentDocumentId,
    groupId,
    onNewDocumentId,
    patientId,
    dataBlockId: blockId,
    templateId,
    afterPublishAction,
    richEdit,
  } = props;

  return useEventCallback(() => {
    function setTabHasUnsavedChanges(hasChanges: boolean) {
      if (tabId) {
        dispatch(
          NavigationActions.tabHasUnsavedChangesChanged({
            id: tabId,
            hasUnsavedChanges: hasChanges,
          }),
        );
      }
    }

    richEdit?.exportToBlob(async (data: Blob) => {
      try {
        const isNewDocument = isDocumentNew(richEdit);
        if (isNewDocument) {
          await unfocusRichEditor();
          const newName = await modals.showPrompt({
            defaultValue: '',
            text: '',
            title: i18n.t('save_new_file_as_name_prompt.title'),
            fieldName: i18n.t('save_new_file_as_name_prompt.text'),
            isMandatory: true,
            maxLength: 200,
            validate: (value: string) => {
              if (/[\/\\]/.test(value)) {
                return i18n.t(
                  'query_builder.errors.file_name_invalid_characters',
                );
              }

              return true;
            },
          });
          if (!newName) {
            return;
          }
          richEdit.fileName = newName;
        }
        let documentId = currentDocumentId;
        if (richEdit.hasUnsavedChanges || isNewDocument) {
          const file = {
            fileName: getFileNameWithFormat(
              richEdit.fileName,
              DXR.DocumentFormat.OpenXml,
            ),
            data: data,
          };
          // it's important to set it before HTTP calls to prevent
          // Bug 41737: Duplicate draft creating on clicking "Ready for PDF" and switching to another tab
          // (another save happens in SaveDocumentRichEditButton from NavigationActions.setSaveChangesHandler)
          richEdit.hasUnsavedChanges = false;
          try {
            if (currentDocumentId) {
              await QueryFactory.FileClient.updateFile(currentDocumentId, file);
            } else {
              const fileInfo = await QueryFactory.FileClient.create(
                FileType.DocumentDraft,
                `Template: ${richEdit.fileName}`,
                blockId,
                patientId,
                null,
                templateId,
                null,
                file,
              );
              documentId = fileInfo.id;
            }
          } catch (e) {
            richEdit.hasUnsavedChanges = true;
            throw e;
          }
          setTabHasUnsavedChanges(false);
          try {
            richEdit.updateRibbon((ribbon) => {
              if (!ribbon) return;
              const newFilePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
              newFilePanel.removeItem(openTemplateMenuId);
            });
          } catch (e) {}
        }
        if (!documentId) {
          return;
        }
        const newVersion =
          await QueryFactory.FileQuery.Client.publishDraft(documentId);
        if (!currentDocumentId) {
          onNewDocumentId?.(documentId);
        }
        //richEdit.hasUnsavedChanges = false;
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.getVersionHistoryQueryKey(groupId!),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.getFileInfoQueryKey(documentId),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
            patientId: patientId,
          }),
        });
        if (blockId) {
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
              dataBlockId: blockId,
            }),
          });
        }
        toast(i18n.t('file_saved_toast'), {
          type: 'success',
        });
        afterPublishAction?.(newVersion);
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    }, DocumentFormatApi.OpenXml);
  });
};
